import { loadStripe, Stripe, StripeError } from '@stripe/stripe-js';
import axios, { AxiosResponse } from 'axios';
import { BaseService } from './BaseService';
import {
    BillingAccount,
    CheckoutSessionParams,
    Invoice,
    OwnedProduct,
    Product,
    StripeSession,
    Subscription,
    PaymentMethod,
    PurchasesResponse,
    Purchase,
} from './types/billing';

export * from './types/billing';

export default class BillingService extends BaseService {
    constructor() {
        super('billing');
    }

    static stripePromise: Promise<Stripe | null> = loadStripe(process.env.VUE_APP_STRIPE_KEY);

    private static getUrl(suffix: string) {
        return `${process.env.VUE_APP_API_URL}v1/billing/${suffix}`;
    }

    /* BillingAccount */

    static async getDefaultBillingAccount(): Promise<BillingAccount> {
        return axios({
            url: this.getUrl('account'),
            method: 'GET',
        }).then((res: AxiosResponse<BillingAccount>) => res.data);
    }

    static async updateBillingAccount(account: BillingAccount): Promise<BillingAccount> {
        // For the time being we check that stripeId is set, just to be safe
        if (!account.stripeId) throw new Error('stripeId needs to be set');

        return axios({
            url: this.getUrl(`account/${account.id}`),
            method: 'PUT',
            data: JSON.stringify(account),
        }).then((res: AxiosResponse<BillingAccount>) => res.data);
    }

    /* Products */

    static async getProductsByTags(tags: Record<string, string>): Promise<Product[]> {
        return axios({
            url: this.getUrl('products/bytags'),
            method: 'POST',
            data: {
                tags: tags,
            },
        }).then((res: AxiosResponse<Product[]>) => {
            return res.data;
        });
    }

    static async getOwnedProducts(): Promise<OwnedProduct[]> {
        return axios({
            url: this.getUrl('products/owned'),
            method: 'GET',
        }).then(res => res.data);
    }

    /* Checkout */

    static async createStripeCheckoutSession(
        params: CheckoutSessionParams,
        billingAccountId?: string
    ): Promise<StripeSession> {
        if (!billingAccountId) {
            const billingAccount = await this.getDefaultBillingAccount();
            billingAccountId = billingAccount.id;
        }

        const returnUrl = !params.returnUrl
            ? window.location.href
            : /^(\?|&|#|\/)/.test(params.returnUrl)
            ? `${window.location.href}${params.returnUrl}`
            : params.returnUrl;

        return axios({
            method: 'POST',
            url: this.getUrl('stripe/checkout'),
            data: JSON.stringify({
                billingAccountId,
                items: params.items,
                returnUrl,
                mode: params.mode || 'payment',
                couponId: params.couponId,
                subscriptionId: params.subscriptionId,
            }),
        }).then((res: AxiosResponse<StripeSession>) => {
            return res.data;
        });
    }

    static async redirectToStripeSession(session: StripeSession): Promise<{ error: StripeError }> {
        const stripe = await BillingService.stripePromise;
        if (!stripe) {
            return Promise.reject();
        }

        return stripe.redirectToCheckout({
            sessionId: session.sessionId,
        });
    }

    /* Subscriptions */

    static async getSubscriptions(): Promise<Subscription[]> {
        const res: AxiosResponse<Subscription[]> = await axios({
            method: 'GET',
            url: this.getUrl('subscriptions'),
        });

        const subs: Subscription[] = res.data.map(sub => ({
            ...sub,
            expiresAt: new Date(sub.expiresAt),
        }));
        return subs;
    }

    static async restartSubscription(id: string): Promise<boolean> {
        const res: AxiosResponse = await axios({
            method: 'POST',
            url: this.getUrl(`subscriptions/${id}/restart`),
        });

        return res.status === 200;
    }

    static async cancelSubscription(id: string): Promise<boolean> {
        const res: AxiosResponse = await axios({
            method: 'POST',
            url: this.getUrl(`subscriptions/${id}/cancel`),
        });

        return res.status === 200;
    }

    /* Invoices */

    static async getNextInvoice(subscriptionId: string): Promise<Invoice> {
        const res: AxiosResponse<Invoice> = await axios({
            method: 'GET',
            url: this.getUrl(`subscriptions/${subscriptionId}/next-invoice`),
        });

        const invoice: Invoice = {
            ...res.data,
            created: new Date(res.data.created),
        };
        return invoice;
    }

    async getAllInvoices(billingAccountId: string): Promise<Invoice[]> {
        const res: AxiosResponse<Invoice[]> = await axios({
            method: 'GET',
            url: this.getUrl(`account/${billingAccountId}/invoices`),
        });

        const invoices = res.data.map<Invoice>(invoice => ({
            ...invoice,
            created: new Date(invoice.created),
            nextPaymentAttempt: new Date(invoice.nextPaymentAttempt),
        }));

        return invoices;
    }

    /* Purschases */

    async getPurchases(billingAccountId: string): Promise<Purchase[]> {
        const res: AxiosResponse<PurchasesResponse> = await axios({
            method: 'GET',
            url: this.getUrl(`account/${billingAccountId}/purchases`),
        });

        return res.data.items.map(purchase => ({
            ...purchase,
            purchasedAt: new Date(purchase.purchasedAt),
        }));
    }

    /* PaymentMethods */

    async getPaymentMethods(billingAccountId: string): Promise<PaymentMethod[]> {
        const res: AxiosResponse<PaymentMethod[]> = await axios({
            method: 'GET',
            url: this.getUrl(`account/${billingAccountId}/payment-methods`),
        });

        return res.data;
    }

    async updateDefaultPaymentMethod(billingAccountId: string, pmId: string): Promise<boolean> {
        return await axios({
            method: 'PUT',
            url: this.getUrl(`account/${billingAccountId}/payment-method/${pmId}`),
        }).then(this.isOK);
    }

    async removePaymentMethod(billingAccountId: string, pmId: string): Promise<boolean> {
        return await axios({
            method: 'DELETE',
            url: this.getUrl(`account/${billingAccountId}/payment-method/${pmId}`),
        }).then(this.isOK);
    }

    async updateDefaultPaymentMethodForSubscription(
        subscriptionId: string,
        pmId: string
    ): Promise<boolean> {
        return await axios({
            method: 'PUT',
            url: this.getUrl(`subscriptions/${subscriptionId}/payment-method/${pmId}`),
        }).then(this.isOK);
    }
}
